import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import moment from "moment";
import { useForm } from "react-hook-form";
import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";
import SnackState from "../../shared-components/snackbar/snackState";
import { httpGet, httpPost } from "../../services/commonService";
import urlConstant from "../../constants/urlConstants";
import dataConstant from "../../constants/dataConstants";
import DataCardGrid from "../../shared-components/DataCardGrid/DataCardGrid";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import CustomAutoCompleteDropdown from "../../shared-components/form-controls/customAutoCompleteDropdown";

const SmartShipInvoice = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accessToken, impersonateEmail } = useContext(AuthenticationContext);
  const [snack, closeSnack, showSnackbar] = SnackState();
  const [reload, setReload] = useState(false);
  const [isInvoiceDeletePopupOpen, setIsInvoiceDeletePopupOpen] =
    useState(false);
  const [runInvoiceDeleteSelectedItem, setRunInvoiceDeleteSelectedItem] =
    useState(null);
  const [isRunInvoicePopupOpen, setIsRunInvoicePopupOpen] = useState(false);
  const [runInvoiceSelectedItem, setRunInvoiceSelectedItem] = useState(null);
  const [membershipCount, setMembershipCount] = useState(0);

  const methods = useForm({
    mode: "all",
    defaultValues: {
      primaryPaymentMethod: {},
      secondaryPaymentMethod: {},
    },
  });

  const {
    setValue,
    handleSubmit,
    control,
    formState: { errors },
  } = methods;

  const handleRunInvoiceDeletePopup = (row) => {
    setRunInvoiceDeleteSelectedItem(row);
    setIsInvoiceDeletePopupOpen(true);
  };

  const handleCloseInvoiceDeletePopup = () => {
    setIsInvoiceDeletePopupOpen(false);
  };

  const handleCloseRunInvoicePopup = () => {
    setIsRunInvoicePopupOpen(false);
  };

  const handleEditMembership = (row) => {
    navigate(`/manageMembership/${row.SmartShipInvoiceID}`);
  };

  const handleRunNowMembership = (row) => {
    const nextProcessDate = moment(row.NextProcessDateUTC).startOf("day");
    const currentDate = moment(new Date()).startOf("day");

    const diffInDays = nextProcessDate.diff(currentDate, "days");
    if (diffInDays > 5) {
      showSnackbar(t("Memberships.RunNowWarning"), "warning");
    } else {
      setRunInvoiceSelectedItem(row);
      setIsRunInvoicePopupOpen(true);
    }
  };

  useEffect(() => {
    getPaymentMethodByUserID();
  }, []);

  const columns = [
    {
      key: "SmartShipInvoiceID",
      label: t("Memberships.InvoiceColumn"),
      type: "text",
    },
    {
      key: "StrInvoiceDate",
      label: t("Memberships.Invoice Date"),
      type: "date",
    },
    {
      key: "StrNextProcessDate",
      label: t("Memberships.DateOfNextOrder"),
      type: "text",
    },
    {
      key: "OrderedItems",
      label: t("Memberships.item"),
      type: "text",
    },
    {
      key: "InvoiceStatus",
      label: t("Memberships.Invoice Status"),
      type: "text",
    },
  ];

  const gridButtons = [
    {
      id: "save",
      label: "Run now",
      icon: "save",
      color: "green",
      onClick: handleRunNowMembership,
    },
    {
      id: "edit",
      label: "Edit",
      icon: "pencil",
      color: "secondary-main",
      onClick: handleEditMembership,
    },
    {
      id: "delete",
      label: "Delete",
      icon: "trash",
      color: "secondary-main",
      onClick: handleRunInvoiceDeletePopup,
    },
  ];

  const fetchDataFunction = async ({
    page,
    pageSize,
    search,
    filters,
    sortKey,
    sortDirection,
  }) => {
    const pagingInfo = {
      PageNumber: page,
      RowsPerPage: pageSize,
      CommonSearchText: search,
      SortColumns: {
        SortColumnName: sortKey,
        SortOrder: sortDirection,
      },
    };
    const requestBody = {
      PagingInfoVM: pagingInfo,
    };
    try {
      const response = await httpPost(
        urlConstant.SmartShipInvoice.SmartShipInvoiceList,
        requestBody,
        true,
        false,
        accessToken,
        impersonateEmail
      );
      if (response?.Value?.Status === dataConstant.APIResponseStatus.Success) {
        const invoiceData = _.map(response.Value.Data.Data, (item) => ({
          ...item,
          isDelete: true,
          isPencil: true,
        }));
        setMembershipCount(response.Value.Data.TotalCount);
        return {
          data: invoiceData,
          totalCount: response.Value.Data.TotalCount,
        };
      } else {
        return { data: [], totalCount: 0 };
      }
    } catch (err) {
      console.error(err);
    }
  };

  const deleteSmartShipInvoice = () => {
    const modelData = {
      Ids: runInvoiceDeleteSelectedItem.SmartShipInvoiceID,
      InvoiceStatus: dataConstant.MemberShipInvoiceStatus.Deleted,
    };
    httpPost(
      urlConstant.SmartShipInvoice.SmartShipInvoiceStatusUpdate,
      modelData,
      true,
      true,
      accessToken,
      impersonateEmail
    )
      .then((response) => {
        if (response.Status === dataConstant.APIResponseStatus.Success) {
          setIsInvoiceDeletePopupOpen(false);
          showSnackbar(response.description, "success");
          setReload(!reload);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const runSmartShipInvoice = async () => {
    await httpGet(
      `${urlConstant.SmartShipInvoice.RunSmartShipInvoice}?Id=${runInvoiceSelectedItem.SmartShipInvoiceID}`,
      true,
      true,
      accessToken,
      impersonateEmail
    )
      .then((response) => {
        if (response.Status === dataConstant.APIResponseStatus.Success) {
          showSnackbar(response.description, "success");
          setTimeout(() => {
            window.location.href = "/membership";
          }, 2000);
        } else if (response.Status === dataConstant.APIResponseStatus.Failure) {
          showSnackbar(response.error, "error");
        }
        setIsRunInvoicePopupOpen(false);
        setReload(!reload);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const formConfig = {
    PrimaryPaymentId: {
      label: t("Memberships.PrimaryPayment"),
      name: "primaryPaymentMethod",
      dropDownList: dataConstant.PrimaryPaymentMethods,
    },
    SecondaryPaymentId: {
      label: t("Memberships.SecondaryPayment"),
      name: "secondaryPaymentMethod",
      dropDownList: dataConstant.SecondaryPaymentMethods,
    },
  };

  const getPaymentMethodByUserID = () => {
    httpGet(
      urlConstant.SmartShipInvoice.GetUserPaymentMethodByUserId,
      true,
      true,
      accessToken,
      impersonateEmail
    )
      .then((response) => {
        if (response.Value.Status === dataConstant.APIResponseStatus.Success) {
          const primaryPaymentMethodList = dataConstant.PrimaryPaymentMethods;
          const secondaryPaymentMethodList =
            dataConstant.SecondaryPaymentMethods;

          if (response.Value.Data.PrimaryPaymentMethodId !== null) {
            const primaryPayment = _.find(primaryPaymentMethodList, (i) => {
              return i.value === response.Value.Data.PrimaryPaymentMethodId;
            });
            if (primaryPayment !== undefined) {
              setValue("primaryPaymentMethod", primaryPayment);
            }
          }

          let secondaryPayment = _.find(secondaryPaymentMethodList, (i) => {
            return i.label === "None";
          });
          if (response.Value.Data.SecondaryPaymentMethodId !== null) {
            secondaryPayment = _.find(secondaryPaymentMethodList, (i) => {
              return i.value === response.Value.Data.SecondaryPaymentMethodId;
            });
          }
          if (secondaryPayment !== undefined) {
            setValue("secondaryPaymentMethod", secondaryPayment);
          }
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const onSubmit = (data) => {
    const model = {
      PrimaryPaymentMethodID: data?.primaryPaymentMethod?.value,
      SecondaryPaymentMethodID: data?.secondaryPaymentMethod?.value,
    };
    httpPost(
      urlConstant.SmartShipInvoice.UserPaymentMethodModify,
      model,
      true,
      true,
      accessToken,
      impersonateEmail
    )
      .then((response) => {
        if (response.Status === dataConstant.APIResponseStatus.Success) {
          showSnackbar(response.description, "success");
        } else if (response.Status === dataConstant.APIResponseStatus.Failure) {
          showSnackbar(response.error, "error");
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const addMembershipEvent = () => {
    if (membershipCount >= 1) {
      showSnackbar(t("Memberships.MembershipLimit"), "warning");
      return;
    }
    navigate("/manageMemberShip");
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
            <Card
              sx={(theme) => ({
                p: {
                  xs: "20px",
                  sm: "25px 30px",
                },
                background: theme.palette.common.black,
                borderRadius: "20px",
                height: "100%",
                flexDirection: "column",
                justifyContent: "space-between",
                display: "flex",
              })}
            >
              <Stack gap={"20px"} direction={"column"}>
                <Stack>
                  <CustomAutoCompleteDropdown
                    methods={methods}
                    properties={{
                      ...formConfig.PrimaryPaymentId,
                      isDisableClearable: true,
                    }}
                  />
                </Stack>
                <Stack>
                  <CustomAutoCompleteDropdown
                    methods={methods}
                    properties={{
                      ...formConfig.SecondaryPaymentId,
                      isDisableClearable: true,
                    }}
                  />
                </Stack>
                <Stack
                  display={"inline-flex"}
                  direction={"row"}
                  flexWrap={"wrap"}
                  gap={"20px"}
                >
                  <Button
                    type="submit"
                    sx={{ py: 1.5, px: 4, width: "auto" }}
                    variant="contained"
                    color="secondary"
                  >
                    {t("Memberships.SavePaymentMethod")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => addMembershipEvent()}
                  >
                    {t("Memberships.AddMembership")}
                  </Button>
                </Stack>
              </Stack>
            </Card>
          </form>
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 8 }}>
          <DataCardGrid
            key={reload}
            onFetchData={fetchDataFunction}
            columns={columns}
            defaultItemsPerPage={8}
            defaultColumnsPerGrid={3}
            enableExport={true}
            enablePagination={true}
            uniqueId="invoice-grid"
            actionButtons={gridButtons}
          />
        </Grid>
      </Grid>

      <Dialog
        open={isInvoiceDeletePopupOpen}
        onClose={handleCloseInvoiceDeletePopup}
        className="login-popup"
        sx={{ borderRadius: "20px", background: "transparent" }}
      >
        <Stack
          sx={(theme) => ({
            background: theme.palette.common.black,
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
          })}
        >
          <DialogTitle>{t("Common.Delete")}</DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleCloseInvoiceDeletePopup}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
              p: 1,
              borderRadius: "8px",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <DialogContent
          sx={{
            background: "var(--background-card)!important",
            border: 0,
          }}
          dividers
        >
          <Stack direction={"column"} alignItems={"center"}>
            <Typography
              color="secondary.text"
              sx={{ fontSize: "16px", marginBottom: "20px" }}
              variant="body2"
            >
              {t("Are you sure you want to delete?")}
            </Typography>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
            <Button
              onClick={() => handleCloseInvoiceDeletePopup()}
              variant="contained"
              color="secondary"
              sx={{ textTransform: "uppercase", width: "100%" }}
            >
              {t("Common.No")}
            </Button>
            <Button
              onClick={() => deleteSmartShipInvoice()}
              variant="contained"
              color="primary"
              sx={{ textTransform: "uppercase", width: "100%" }}
            >
              {t("Common.Yes")}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isRunInvoicePopupOpen}
        onClose={handleCloseRunInvoicePopup}
        className="login-popup"
        sx={{ borderRadius: "20px", background: "transparent" }}
      >
        <Stack
          sx={(theme) => ({
            background: theme.palette.common.black,
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
          })}
        >
          <DialogTitle>{t("Confirmation")}</DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleCloseRunInvoicePopup}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
              p: 1,
              borderRadius: "8px",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <DialogContent
          sx={{
            background: "var(--background-card)!important",
            border: 0,
          }}
          dividers
        >
          <Stack direction={"column"} alignItems={"center"}>
            <Typography
              color="secondary.text"
              sx={{ fontSize: "16px", marginBottom: "5px" }}
              variant="body2"
            >
              {t("Memberships.RunInvoiceProcess")}
            </Typography>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} mt={"10px"} gap={2}>
            <Button
              onClick={() => handleCloseRunInvoicePopup()}
              variant="contained"
              color="secondary"
              sx={{ textTransform: "uppercase", width: "100%" }}
            >
              {t("Common.No")}
            </Button>
            <Button
              onClick={() => runSmartShipInvoice()}
              variant="contained"
              color="primary"
              sx={{ textTransform: "uppercase", width: "100%" }}
            >
              {t("Common.Yes")}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} buttonProp />
    </>
  );
};

export default SmartShipInvoice;